import {
    useLayoutEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    TextInput,
    Text,
    Button,
    Dialog,
    NCoreUIKitToast
} from "ncore-ui-kit";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation,
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    SendContactFormRequestType
} from "../../../services/restAPI/actions/sendContactForm/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    UserIcon,
    Phone,
    Mail,
    Check
} from "lucide-react-native";
import validator from "validator";
import {
    RESTService
} from "../../../services/restAPI";

const ContactForm = () => {
    const {
        windowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [data,
        setData] = useState<SendContactFormRequestType>({
            fullName: "",
            mail: "",
            message: "",
            phoneNumber: "",
            subject: ""
        });
    const [loading,
        setLoading] = useState(false);
    const [successDialog,
        setSuccessDialog] = useState(false);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("contact-form")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const submitForm = () => {
        setLoading(true);
        const params: SendContactFormRequestType = {
            fullName: data.fullName.trim(),
            phoneNumber: data.phoneNumber.trim(),
            mail: data.mail.trim(),
            subject: data.subject.trim(),
            message: data.message.trim()
        };
        console.log(params);
        RESTService.action("SendContactForm", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(()=>{
                setLoading(false);
            });
    };

    const renderInfo = () => {
        return <View
            style={[
                stylesheet.infoCardContainer,
                {
                    backgroundColor: colors.content.container.mid,
                    width: windowWidth - spaces.spacingLg * 2,
                    marginTop: spaces.spacingLg,
                    borderRadius: radiuses.sm,
                    padding: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.iconContent,
                    {
                        backgroundColor: colors.content.container.success,
                        borderRadius: radiuses.full
                    }
                ]}
            >
                <UserIcon
                    color={colors.content.icon.success}
                    size={30}
                />
            </View>
            <View
                style={[
                    stylesheet.textWrapper,
                    {
                        paddingHorizontal: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("contact-us")}
                </Text>
                <Text
                    variant="titleSmallSize"
                    color="low"
                >
                    {localize("contact-us-info")}
                </Text>
            </View>
        </View>;
    };

    const isFormValid = Boolean(
        data.fullName && data.fullName.trim().length > 0 &&
        data.phoneNumber && data.phoneNumber.trim().length > 0 &&
        data.mail && data.mail.trim().length > 0 && validator.isEmail(data.mail.trim()) &&
        data.subject && data.subject.trim().length > 0 &&
        data.message && data.message.trim().length > 0
    );

    const renderForm = () => {
        return <View
            style={{
                marginTop: spaces.spacingLg
            }}
        >
            <TextInput
                title={localize("name-surname")}
                type={data.fullName && data.fullName.trim().length === 0 ? "danger" : "default"}
                hintText={data.fullName !== undefined && data.fullName.trim().length === 0 ? (localize("nameRequired")) : undefined}
                spreadBehaviour="stretch"
                placeholder={localize("name-surname")}
                isDisabled={loading}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        fullName: e
                    });
                }}
                icon={() => <UserIcon
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("phone")}
                type={data.phoneNumber && data.phoneNumber.trim().length === 0 ? "danger" : "default"}
                hintText={data.phoneNumber !== undefined && data.phoneNumber.trim().length === 0 ? (localize("phoneRequired")) : undefined}
                spreadBehaviour="stretch"
                placeholder={"+90 555 555 55 55"}
                isDisabled={loading}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        phoneNumber: e
                    });
                }}
                icon={() => <Phone
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("e-mail")}
                type={data.mail !== undefined && data.mail.trim().length > 0 && !validator.isEmail(data.mail.trim()) ? "danger" : "default"}
                hintText={data.mail !== undefined && data.mail.trim().length > 0 && !validator.isEmail(data.mail.trim()) ? (localize("invalidEmail")) : undefined}
                spreadBehaviour="stretch"
                placeholder={localize("e-mail")}
                isDisabled={loading}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        mail: e
                    });
                }}
                icon={() => <Mail
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("subject")}
                type={data.subject && data.subject.trim().length === 0 ? "danger" : "default"}
                hintText={data.subject !== undefined && data.subject.trim().length === 0 ? (localize("subjectRequired")) : undefined}
                spreadBehaviour="stretch"
                placeholder={localize("subject")}
                isDisabled={loading}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        subject: e
                    });
                }}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextAreaInput
                title={localize("message")}
                type={data.message && data.message.trim().length === 0 ? "danger" : "default"}
                hintText={data.message !== undefined && data.message.trim().length === 0 ? (localize("messageRequired")) : undefined}
                spreadBehaviour="stretch"
                isDisabled={loading}
                placeholder={localize("message")}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        message: e
                    });
                }}
                maxHeight={150}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <View
                style={stylesheet.checkboxContainer}
            >
                <View
                    style={[
                        stylesheet.infoContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: windowWidth / 2 - spaces.spacingMd * 2,
                            borderRadius: radiuses.sm,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Phone
                        color={colors.content.icon.low}
                        size={18}
                    />
                    <View
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="titleSmallSize"
                            color="low"
                        >
                            {localize("phone")}
                        </Text>
                        <Text
                            variant="titleSmallSize"
                            color="high"
                        >
                            {"0332 000 00 00 "}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.infoContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: windowWidth / 2 - spaces.spacingMd * 2,
                            marginLeft: spaces.spacingLg,
                            borderRadius: radiuses.sm,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Mail
                        color={colors.content.icon.low}
                        size={18}
                    />
                    <View
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="titleSmallSize"
                            color="low"
                        >
                            {localize("e-mail")}
                        </Text>
                        <Text
                            variant="titleSmallSize"
                            color="high"
                        >
                            {"info@musiad.org"}
                        </Text>
                    </View>
                </View>
            </View>
            <Button
                title={localize("submit")}
                type="primary"
                size="large"
                customColor="brandPrimary"
                customBorderColor="brandPrimary"
                spreadBehaviour="stretch"
                style={[
                    stylesheet.submitButton,
                    {
                        marginVertical: spaces.spacingXl
                    }
                ]}
                isLoading={loading}
                isDisabled={!isFormValid}
                onPress={() => {
                    if(!isFormValid) {
                        NCoreUIKitToast.open({
                            title: localize("error"),
                            subTitle: localize("pleaseFillAllFields"),
                            type: "danger"
                        });
                        return;
                    }
                    submitForm();
                }}
            />
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeader}
            >
                <View
                    style={[
                        stylesheet.successIconContainer,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            isVisible={successDialog}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.goBack();
            }}
            primaryButtonProps={{
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.goBack();
                },
                title: localize("backToHome"),
                type: "success"
            }}
            variant="ok"
        />;
    };

    return <PageContainer
        backgroundColor="subtle"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderInfo()}
        {renderForm()}
        {renderSuccessDialog()}
    </PageContainer>;
};
export default ContactForm;
